import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { firebaseAuth, isFirebaseConfigured } from "@/lib/firebase"
import {
  AUTH_DEV_BYPASS,
  USE_MOCKS,
  api,
  setActiveEntityId as publicarEntidadActiva,
  setActiveTenantHeader,
} from "@/lib/api"
import { decidirTenant } from "@/lib/activeTenant"
import {
  aplicarEntidadActiva,
  elegirEntidad,
  guardarEntidad,
  leerEntidadGuardada,
} from "@/lib/activeEntity"
import { fetchEntities, type Entity } from "@/lib/entities"
import { type PerfilRow, profileFromApi } from "@/lib/authMapping"
import type { Capability, Membership, Role, UserProfile } from "@/lib/types"

const ACTIVE_TENANT_KEY = "cfo.activeTenantId"
const MOCK_SESSION_KEY = "cfo.mockSession"

/**
 * Perfil mock mientras el backend no expone GET /api/auth/me.
 * Contador cross-tenant: es el rol que sostiene el modelo de negocio.
 */
const MOCK_PROFILE: UserProfile = {
  uid: "mock-uid",
  email: "contador@estudio.pe",
  displayName: "Rosa Quispe",
  global: false,
  idioma: null,
  globalRoles: [],
  memberships: [
    {
      tenantId: "t-001",
      tenantName: "Andina Servicios SAC",
      country: "PE",
      roles: ["accountant"],
      role: "accountant",
      capabilities: ["read", "edit", "approve", "close_period", "sign"],
      tenantWide: true,
    },
    {
      tenantId: "t-002",
      tenantName: "Nexo Digital SpA",
      country: "CL",
      roles: ["accountant"],
      role: "accountant",
      capabilities: ["read", "edit", "approve", "close_period", "sign"],
      tenantWide: true,
    },
    {
      tenantId: "t-003",
      tenantName: "Corporacao Vela Ltda",
      country: "BR",
      roles: ["accountant"],
      role: "accountant",
      capabilities: ["read", "edit", "approve", "close_period"],
      tenantWide: true,
    },
  ],
}

interface AuthContextValue {
  firebaseUser: User | null
  profile: UserProfile | null
  loading: boolean
  error: string | null
  activeTenantId: string | null
  setActiveTenantId: (tenantId: string) => void
  /**
   * Sociedad activa dentro del tenant. `null` significa "todavia no se sabe":
   * ninguna pantalla que necesite entidad debe llamar a la API con esto en null
   * (ver `components/layout/RequireEntity.tsx`).
   */
  activeEntityId: string | null
  setActiveEntityId: (entityId: string) => void
  /** Sociedades visibles del tenant activo. */
  entities: Entity[]
  entitiesLoading: boolean
  entitiesError: unknown
  refetchEntities: () => void
  /**
   * Vuelve a pedir `/auth/me`.
   *
   * Hace falta porque el perfil lleva preferencias que se cambian desde la
   * propia SPA —hoy el idioma de la interfaz— y el perfil es SU fuente. Sin
   * esto habría que guardar el mismo dato en dos sitios y mantenerlos de
   * acuerdo, que es como se acaba con la pantalla en un idioma y el servidor
   * creyendo que está en otro.
   */
  refreshProfile: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  /**
   * Crea la IDENTIDAD, y nada más.
   *
   * El alta de la empresa es otra llamada —`POST /auth/registro`— y va aparte a
   * propósito: Firebase dice quién eres, y quién es cliente de qué lo decide el
   * backend. Juntarlas aquí metería una regla de negocio dentro del proveedor de
   * identidad, que es donde nadie la va a buscar.
   */
  signUpWithEmail: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * El backend resuelve el perfil (rol x alcance x capacidad) desde Postgres.
 * TODO(backend): GET /api/auth/me — reemplaza el perfil mock.
 */
async function fetchProfile(): Promise<UserProfile> {
  if (USE_MOCKS) return MOCK_PROFILE
  // `/auth/me` responde en el vocabulario del backend (`alcances`, `rol`,
  // `tenant_nombre`) y con roles en espanol. Traducir aqui y no en cada
  // pantalla: ver `authMapping.ts`.
  return profileFromApi(await api.get<PerfilRow>("/auth/me"))
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTenantId, setActiveTenantIdState] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_TENANT_KEY),
  )

  const loadProfile = useCallback(async () => {
    try {
      const next = await fetchProfile()
      setProfile(next)
      setError(null)
      return next
    } catch (err) {
      setProfile(null)
      setError(err instanceof Error ? err.message : "No se pudo cargar el perfil.")
      return null
    }
  }, [])

  /* Sesion sin Firebase configurado. Sirve tanto para el modo mock como
     para desarrollo contra el backend real con VITE_AUTH_DEV_BYPASS. */
  useEffect(() => {
    if (isFirebaseConfigured && firebaseAuth) return
    const hasMockSession = localStorage.getItem(MOCK_SESSION_KEY) === "1"
    if (!hasMockSession) {
      setLoading(false)
      return
    }
    void loadProfile().finally(() => setLoading(false))
  }, [loadProfile])

  useEffect(() => {
    if (!firebaseAuth) return
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
      setFirebaseUser(user)
      if (!user) {
        setProfile(null)
        setLoading(false)
        return
      }
      await loadProfile()
      setLoading(false)
    })
    return unsubscribe
  }, [loadProfile])

  /* El tenant activo viaja en cada request: es lo que activa RLS en el backend.
     La REGLA vive en `lib/activeTenant.ts`, sin React, para poder probarla:
     este efecto solo la aplica. Ver alli por que "todavia no cargo" y "no tiene
     alcance" tienen que tratarse distinto. */
  useEffect(() => {
    const decision = decidirTenant(profile, activeTenantId)
    if (!decision.publicar) return

    if (decision.activo !== activeTenantId) {
      setActiveTenantIdState(decision.activo)
      if (decision.activo) localStorage.setItem(ACTIVE_TENANT_KEY, decision.activo)
    }
    setActiveTenantHeader(decision.header)
  }, [profile, activeTenantId])

  const setActiveTenantId = useCallback((tenantId: string) => {
    setActiveTenantIdState(tenantId)
    localStorage.setItem(ACTIVE_TENANT_KEY, tenantId)
    setActiveTenantHeader(tenantId)
  }, [])

  /* ======================================================================
   * Entidad (sociedad) activa
   * ======================================================================
   * Segunda coordenada del alcance. Va aca y no en cada pantalla por la misma
   * razon que el tenant: es sesion, no filtro. La logica de resolucion y
   * persistencia vive en `lib/activeEntity.ts`, que se puede probar sin React.
   */
  const [activeEntityId, setActiveEntityIdState] = useState<string | null>(null)

  /* Ids que el propio perfil acota (alcance por sociedad). Segunda fuente
     cuando la bandeja no ve la entidad: ver `lib/entities.ts`. */
  const entityIdsEnAlcance = useMemo(() => {
    const membership = profile?.memberships.find((m) => m.tenantId === activeTenantId)
    return membership?.entityIds ?? []
  }, [profile, activeTenantId])

  const entitiesQuery = useQuery({
    queryKey: ["entities", activeTenantId, entityIdsEnAlcance],
    queryFn: () => fetchEntities(activeTenantId, entityIdsEnAlcance),
    enabled: Boolean(activeTenantId) && Boolean(profile),
    // El catalogo de sociedades de un cliente no cambia mientras se trabaja.
    staleTime: 5 * 60_000,
  })

  const entities = useMemo(() => entitiesQuery.data ?? [], [entitiesQuery.data])

  /* Cambio de cliente: la sociedad anterior deja de valer EN EL ACTO, sin
     esperar a que llegue la lista nueva. Si esperara, una peticion disparada
     en ese hueco llevaria la entidad del cliente anterior. No se escribe en
     localStorage aca: leer no debe borrar lo guardado. */
  useEffect(() => {
    const guardada = leerEntidadGuardada(activeTenantId)
    setActiveEntityIdState(guardada)
    publicarEntidadActiva(guardada)
  }, [activeTenantId])

  /* Cuando ya se sabe que sociedades hay: se valida la guardada, se
     autoselecciona si hay una sola, y se persiste el resultado. */
  useEffect(() => {
    if (!entitiesQuery.isSuccess) return
    setActiveEntityIdState(aplicarEntidadActiva(activeTenantId, entities))
  }, [activeTenantId, entities, entitiesQuery.isSuccess])

  /* Cambiar de cliente o de sociedad cambia DE QUIEN son los numeros. Lo que
     quedo en cache lo pidio otro alcance: servirlo mientras llega el refetch
     mostraria las cifras de una sociedad bajo el nombre de otra, y un total
     ajeno se lee igual de rapido que el propio. Se descarta la cache entera
     menos el catalogo de sociedades, que es quien dispara esto.

     `resetQueries` y no `removeQueries`: quitar del cache una consulta que
     tiene la pantalla montada la deja sin datos y sin nadie que los vuelva a
     pedir (se vio: la tabla de clientes quedaba vacia para siempre). `reset`
     limpia y ademas relanza las que estan en uso.

     Solo cuando el alcance ya estaba RESUELTO y cambia: la resolucion inicial
     —de null al tenant, de null a la sociedad— no es un cambio de alcance y
     resetear ahi solo duplicaba peticiones al abrir cualquier pantalla. */
  const queryClient = useQueryClient()
  const alcanceAnterior = useRef<{ tenant: string | null; entity: string | null } | null>(null)
  useEffect(() => {
    const anterior = alcanceAnterior.current
    alcanceAnterior.current = { tenant: activeTenantId, entity: activeEntityId }
    if (!anterior) return

    const cambioDeCliente = Boolean(anterior.tenant) && anterior.tenant !== activeTenantId
    const cambioDeSociedad = Boolean(anterior.entity) && anterior.entity !== activeEntityId

    /*
      EL CAMBIO DE CLIENTE NO PUEDE DEPENDER DE QUE HUBIERA SOCIEDAD ELEGIDA.

      La condición era `if (!anterior?.tenant || !anterior?.entity) return`, o
      sea que exigía una sociedad ANTERIOR para resetear nada. Y un cliente con
      varias sociedades arranca **sin ninguna elegida** —`activeEntityId` es
      `null` hasta que alguien la elige, porque autoseleccionar con cuatro sería
      decidir por el usuario—. Resultado: en el estado por defecto, **cambiar de
      cliente no limpiaba la caché**.

      Medido antes del arreglo: entrando como Siete (cuatro sociedades, ninguna
      elegida) y cambiando a «Cliente Nuevo», el paso 1 del traspaso seguía
      listando las cuatro sociedades de SIETE bajo el nombre del cliente nuevo
      —a los 10 segundos seguían ahí—, y sólo desaparecían recargando la página.

      Es el defecto más caro que puede tener un producto multi-cliente: no falla,
      no avisa, y enseña los datos de un cliente bajo el nombre de otro. El
      backend estaba bien —RLS devuelve cero sociedades para el cliente nuevo—,
      así que no había forma de notarlo desde el servidor.

      Ahora las dos condiciones son independientes: cambiar de cliente resetea
      SIEMPRE, y cambiar de sociedad resetea cuando ya había una. Lo que se sigue
      respetando es la resolución inicial —de `null` al primer valor—, que no es
      un cambio de alcance y resetear ahí sólo duplicaba peticiones.
    */
    if (!cambioDeCliente && !cambioDeSociedad) return
    queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== "entities" })
  }, [queryClient, activeTenantId, activeEntityId])

  const setActiveEntityId = useCallback(
    (entityId: string) => {
      setActiveEntityIdState(entityId)
      elegirEntidad(activeTenantId, entityId)
    },
    [activeTenantId],
  )

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      setError(null)
      if (!firebaseAuth) {
        if (!USE_MOCKS && !AUTH_DEV_BYPASS)
          throw new Error("Firebase no esta configurado.")
        localStorage.setItem(MOCK_SESSION_KEY, "1")
        setLoading(true)
        await loadProfile()
        setLoading(false)
        return
      }
      await signInWithEmailAndPassword(firebaseAuth, email, password)
    },
    [loadProfile],
  )

  const signUpWithEmail = useCallback(
    async (email: string, password: string) => {
      setError(null)
      if (!firebaseAuth) {
        if (!USE_MOCKS && !AUTH_DEV_BYPASS)
          throw new Error("Firebase no esta configurado.")
        // Sin Firebase no hay a quien dar de alta: la sesion local ya vale como
        // identidad y el registro sigue siendo el alta de la EMPRESA, que es la
        // llamada de despues. Asi el registro se puede recorrer entero en
        // desarrollo sin tocar el proyecto de Firebase.
        localStorage.setItem(MOCK_SESSION_KEY, "1")
        setLoading(true)
        await loadProfile()
        setLoading(false)
        return
      }
      await createUserWithEmailAndPassword(firebaseAuth, email, password)
    },
    [loadProfile],
  )

  const signInWithGoogle = useCallback(async () => {
    setError(null)
    if (!firebaseAuth) {
      if (!USE_MOCKS && !AUTH_DEV_BYPASS)
          throw new Error("Firebase no esta configurado.")
      localStorage.setItem(MOCK_SESSION_KEY, "1")
      setLoading(true)
      await loadProfile()
      setLoading(false)
      return
    }
    await signInWithPopup(firebaseAuth, new GoogleAuthProvider())
  }, [loadProfile])

  const signOut = useCallback(async () => {
    localStorage.removeItem(MOCK_SESSION_KEY)
    setProfile(null)
    setFirebaseUser(null)
    setActiveTenantHeader(null)
    // La sociedad tambien se olvida: el proximo que entre en este navegador no
    // hereda el alcance del anterior.
    setActiveEntityIdState(null)
    guardarEntidad(null, null)
    publicarEntidadActiva(null)
    if (firebaseAuth) await firebaseSignOut(firebaseAuth)
  }, [])

  const refetchEntities = useCallback(() => {
    void entitiesQuery.refetch()
  }, [entitiesQuery])

  const value = useMemo<AuthContextValue>(
    () => ({
      firebaseUser,
      profile,
      loading,
      error,
      activeTenantId,
      setActiveTenantId,
      activeEntityId,
      setActiveEntityId,
      entities,
      entitiesLoading: entitiesQuery.isPending && entitiesQuery.fetchStatus !== "idle",
      entitiesError: entitiesQuery.error,
      refetchEntities,
      refreshProfile: async () => { await loadProfile() },
      signInWithEmail,
      signInWithGoogle,
      signUpWithEmail,
      signOut,
    }),
    [
      firebaseUser,
      profile,
      loading,
      error,
      activeTenantId,
      setActiveTenantId,
      activeEntityId,
      setActiveEntityId,
      entities,
      entitiesQuery.isPending,
      entitiesQuery.fetchStatus,
      entitiesQuery.error,
      refetchEntities,
      loadProfile,
      signInWithEmail,
      signInWithGoogle,
      signUpWithEmail,
      signOut,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error("useAuth debe usarse dentro de <AuthProvider>.")
  return context
}

export interface RbacHelpers {
  profile: UserProfile | null
  memberships: Membership[]
  activeMembership: Membership | null
  role: Role | null
  /** Todos los roles de la persona, en todas sus membresias. */
  roles: Role[]
  capabilities: Capability[]
  /** Capacidad sobre un tenant (por defecto, el activo). */
  can: (capability: Capability, tenantId?: string) => boolean
  hasRole: (...roles: Role[]) => boolean
  /** Alcance sobre mas de un tenant: contador, CFO o admin de plataforma. */
  isCrossTenant: boolean
  /**
   * Alcance sobre el TENANT entero en la empresa activa, no solo sobre una de
   * sus sociedades. Es el segundo eje que gatea el menu —el primero es el rol—
   * y lo necesita `lib/secciones.ts` para no ofrecer una puerta que el backend
   * no abre.
   *
   * A diferencia de `roles`, que se unen de TODAS las membresias, esto mira la
   * ACTIVA: el alcance es por tenant, y ser contador del grupo entero en una
   * empresa no da alcance de grupo en otra.
   */
  tenantWide: boolean
  isReadOnly: boolean
  /**
   * Separacion de funciones: quien prepara no puede firmar.
   * Sin esto, el sign-off del contador es decorativo.
   */
  canSignPreparedBy: (preparedBy: string, tenantId?: string) => boolean
}

export function useRbac(): RbacHelpers {
  const { profile, activeTenantId } = useAuth()

  return useMemo(() => {
    const memberships = profile?.memberships ?? []
    const activeMembership =
      memberships.find((m) => m.tenantId === activeTenantId) ?? memberships[0] ?? null

    const can = (capability: Capability, tenantId?: string) => {
      const target = tenantId ?? activeTenantId
      const membership = target
        ? memberships.find((m) => m.tenantId === target)
        : activeMembership
      if (!membership) return false
      return membership.capabilities.includes(capability)
    }

    // Se miran TODOS los roles de la membresia activa, no solo el principal:
    // quien es contador y CFO de la misma empresa responde que si a los dos.
    const hasRole = (...roles: Role[]) =>
      activeMembership ? activeMembership.roles.some((r) => roles.includes(r)) : false

    return {
      profile,
      memberships,
      activeMembership,
      role: activeMembership?.role ?? null,
      // Todos los roles de la persona, no solo el de la sociedad activa ni solo
      // el principal de cada membresia: quien es CFO y contador a la vez usa las
      // dos secciones, y esa es exactamente la lista que gatea el menu.
      // Los globales entran aqui y no en `memberships`: el admin de plataforma
      // no es miembro de ningun cliente, pero su rol si abre secciones.
      roles: Array.from(
        new Set([...memberships.flatMap((m) => m.roles), ...(profile?.globalRoles ?? [])]),
      ),
      capabilities: activeMembership?.capabilities ?? [],
      can,
      hasRole,
      isCrossTenant: memberships.length > 1 || Boolean(profile?.global),
      // El admin de plataforma alcanza cualquier tenant aunque no tenga fila
      // propia: su asignacion es global y `es_global` es como llega.
      tenantWide: Boolean(profile?.global) || Boolean(activeMembership?.tenantWide),
      isReadOnly: !can("edit"),
      canSignPreparedBy: (preparedBy: string, tenantId?: string) =>
        can("sign", tenantId) && preparedBy !== profile?.displayName,
    }
  }, [profile, activeTenantId])
}
